import {
  H3,
  H4,
  mediaHelper,
  paletteHelper,
  radiusHelper,
  spacingHelper,
  typographyHelper,
} from '@property.com.au/ui-kit';
import styled from 'styled-components';
import { LegacyCTABannerDetails } from '../GovernmentDataPartnerBannerWithDataV2';
import { WebLink } from '../../../../../../app-kit/components/WebLink/WebLink';
import allianzLogo from '../../../../../../assets/allianz.svg';
import { DataGetter, trackEvent } from '@rea-argonaut/analytics';
import { deepOmitTypeName } from '../../../../../../analytics/helpers/deepOmitTypeName';

// This is a copy of the (original) GovernmentDataPartnerBanner that is still used for NT.
// It was however modified to use the same `trackBannerClick` as the new SingleCTABanner
// so that all tracking uses the new content and tracking fields and behaves exactly the same.
//
// Its supposed to be a quick temp band-aid for Allianz to see if this legacy style converted better,
// but is tracked exactly the same as the new SingleCTABanner,
// Context here:
//  - https://rea-group.slack.com/archives/C06QGU10MRB/p1784504707566809
//  - https://rea-group.slack.com/archives/C06QGU10MRB/p1784507361328309

const Card = styled.div`
  background-color: ${paletteHelper('backgroundInformation')};
  border-radius: ${radiusHelper('large')};
  display: flex;
  flex-direction: column;
  padding: ${spacingHelper('large')};
  margin-bottom: ${spacingHelper('small')};
  gap: ${spacingHelper('twoExtraSmall')};

  ${mediaHelper('mobile')} {
    padding: ${spacingHelper('medium')};
  }
`;

const Description = styled(H4)`
  ${mediaHelper('tabletMin')} {
    max-width: calc(${spacingHelper('extraLarge')} * 16);
  }
`;

const ButtonContainer = styled.div`
  margin-top: ${spacingHelper('medium')};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  row-gap: 1.25rem;

  ${mediaHelper('tabletMin')} {
    flex-direction: row;
  }
`;

const StyledWebLink = styled(WebLink)`
  width: auto;

  /* Allianz brand colour */
  background-color: rgb(0 55 129 / 100%);
  &:hover,
  &:focus,
  &:focus-visible,
  &:active {
    background-color: rgb(0 55 129 / 80%);
  }

  ${mediaHelper('tabletMin')} {
    order: -1;
  }
`;

const Logo = styled.img`
  width: 9rem;
  height: 2.5rem;
`;

const Disclaimer = styled.div`
  ${typographyHelper('caption')};
  color: ${paletteHelper('textSecondary')};
`;

const trackBannerClick = (linkTracking: LegacyCTABannerDetails['linkTracking']): void => {
  if (!linkTracking) return;
  const event: DataGetter = () => deepOmitTypeName(linkTracking);
  trackEvent(event);
};

interface LegacyCTABanner {
  bannerDetails: LegacyCTABannerDetails;
}

export const LegacyCTABanner = ({ bannerDetails }: LegacyCTABanner) => {
  const { title, description, link, disclaimer, linkTracking } = bannerDetails;

  return (
    <>
      <Card data-testid="government-data-partner-banner-v2">
        <H3 variant="body01Bold">{title}</H3>
        <Description variant="subtitle02">{description}</Description>
        <ButtonContainer>
          <Logo loading="lazy" alt="Allianz Logo" src={allianzLogo} />

          <StyledWebLink
            sizeVariant="medium"
            variant="button-primary"
            url={link.url}
            isExternal={link.isExternal}
            title={link.title}
            tracking={link.tracking}
            onClick={() => trackBannerClick(linkTracking)}
          />
        </ButtonContainer>
      </Card>
      <Disclaimer>{disclaimer}</Disclaimer>
    </>
  );
};
